import { cn } from "@/lib/utils";
import { Eye, Pencil } from "lucide-react";

/** The admin's Edit ⇄ Preview switch as ONE segmented control — not a state label sitting beside a button
 *  (which reads as two separate things). "Edit" makes the surface live-editable; "Preview" flips it to the
 *  exact read-only client render, so what's published is provable without leaving the page. The active half
 *  is the app's solid inverted pill — the same "selected" language as the nav tab, the filter chips, and the
 *  account-menu segments — so one glance shows which mode you're in. Shared by the role-page header and the
 *  candidate drawer so the one affordance is identical everywhere and can't drift. `editing` is the current
 *  state; `onToggle` flips it — clicking the already-active half is a no-op. */
export function EditPreviewToggle({ editing, onToggle }: { editing: boolean; onToggle: () => void }) {
  const seg =
    "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring";
  const active = "bg-foreground text-background shadow-sm";
  const idle = "text-muted-foreground hover:text-foreground";
  return (
    <div className="inline-flex rounded-lg bg-muted p-0.5">
      <button
        type="button"
        aria-pressed={editing}
        onClick={() => {
          if (!editing) onToggle();
        }}
        title="Edit this page"
        className={cn(seg, editing ? active : idle)}
      >
        <Pencil className="size-3.5" aria-hidden /> Edit
      </button>
      <button
        type="button"
        aria-pressed={!editing}
        onClick={() => {
          if (editing) onToggle();
        }}
        title="See exactly what the client sees"
        className={cn(seg, !editing ? active : idle)}
      >
        <Eye className="size-3.5" aria-hidden /> Preview
      </button>
    </div>
  );
}
